import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { GridColDef, GridCellParams } from "@mui/x-data-grid";
import { Box } from "@mui/material";
import { Formik, Form } from "formik";
import Grid from '@mui/material/Grid';
import * as Yup from "yup";
import SearchIcon from "@mui/icons-material/Search";
import SettingsIcon from "@mui/icons-material/Settings";
import CompostIcon from '@mui/icons-material/Compost';
import SpaOutlinedIcon from "@mui/icons-material/SpaOutlined";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete"
import TextField from '../../components/TextField';
import GridTable from "../../components/GridTable";
import ActionMenu from "../../components/ActionMenu";
import Dialog from "../../components/Dialog";
import Loader from "../../components/Loader";
import Button from "../../components/Button";
import { successMessage, errorMessage } from "../../components/Toaster";
import { getAllCrops, createCrop, updateCrop, deleteCrop } from "./api";
import { ApiResponse, CropData, CreateCropParams } from './interface';
import { getErrorMessage } from "../../utils/error";

const Crop: React.FC = () => {
    const navigate = useNavigate();
    const [crops, setCrops] = useState<CropData[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [editCrop, setEditCrop] = useState<CropData | null>(null);
    const [isDialogOpen, setDialogOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<GridCellParams | null>(null);
    const [loading, setLoading] = useState({
        fetch: false,
        create: false,
        update: false,
        delete: false,
    });

    const validationSchema = Yup.object().shape({
        cropName: Yup.string().required('Crop name is required'),
    });

    const columns: GridColDef[] = [
        { field: 'crop_name', headerName: 'Crop Name', flex: 1 },
        {
            field: "actions",
            headerName: "Actions",
            renderCell: (params: GridCellParams) => (
                <ActionMenu
                    params={params}
                    actions={[
                        {
                            icon: <EditIcon color="primary" />,
                            tooltip: "Edit",
                            onClick: () => handleEdit(params),
                        },
                        {
                            icon: <DeleteIcon color="error" />,
                            tooltip: "Delete",
                            onClick: () => handleDelete(params),
                        },
                        {
                            icon: <SpaOutlinedIcon color="success" />,
                            tooltip: "Variety",
                            onClick: handleViewVariety,
                        },
                        {
                            icon: <SettingsIcon color="inherit" />,
                            tooltip: "Crop Config",
                            onClick: handleViewConfig,
                        },
                    ]}
                />
            ),
            sortable: false,
            filterable: false,
            flex: 1,
        },
    ];

    useEffect(() => {
        const fetchCrops = async () => {
            setLoading(prev => ({ ...prev, fetch: true }));
            try {
                const res: ApiResponse<CropData[]> = await getAllCrops();
                if (res.status && res.data) {
                    setCrops(res.data);
                } else {
                    errorMessage(res.message || "Failed to fetch crops.");
                }
            } catch (error) {
                errorMessage(getErrorMessage(error) || "Error fetching crops");
            } finally {
                setLoading(prev => ({ ...prev, fetch: false }));
            }
        };

        fetchCrops();
    }, []);

    const openDialog = () => {
        setDialogOpen(true);
    };

    const closeDialog = (resetForm: () => void) => {
        resetForm();
        setEditCrop(null);
        setDialogOpen(false);
    };

    const handleEdit = (params: GridCellParams) => {
        setEditCrop(params.row as CropData);
        openDialog();
    };

    const handleDelete = (params: GridCellParams) => {
        setItemToDelete(params);
        setDeleteOpen(true);
    };

    const handleDeleteCancel = () => {
        setDeleteOpen(false);
        setItemToDelete(null);
    };

    const handleViewVariety = (params: GridCellParams) => {
        const crop_id = params.row.crop_id;
        const crop_name = params.row.crop_name;
        navigate(`/crops/${crop_id}/varieties`, {
            state: { crop_name }
        });
    };

    const handleViewConfig = (params: GridCellParams) => {
        const crop_id = params.row.crop_id;
        const crop_name = params.row.crop_name;
        navigate(`/crops/${crop_id}/${crop_name}/crop_config`);
    };

    const confirmDialog = async (
        values: any,
        { resetForm }: { resetForm: () => void }
    ) => {
        let success = false;
        if (editCrop) {
            success = await handleUpdate(values);
        } else {
            success = await handleCreate(values);
        }
        if (success) {
            closeDialog(resetForm);
        }
    };

    const handleCreate = async (values: { cropName: string; description: string }) => {
        setLoading(prev => ({ ...prev, create: true }));
        try {
            const newCrop: CreateCropParams = {
                crop_name: values.cropName,
            };
            const response = await createCrop(newCrop);
            if (response.status) {
                const newCrop = {
                    crop_id: response.data!.crop_id,
                    crop_name: values.cropName
                };
                setCrops((prev) => [...prev, newCrop]);
                successMessage(response.message);
                return true;
            } else {
                errorMessage(response.message || 'Failed to create crop.');
                return false;
            }
        } catch (error) {
            errorMessage(getErrorMessage(error) || 'Unexpected error occurred.');
            return false;
        } finally {
            setLoading(prev => ({ ...prev, create: false }));
        }
    };

    const handleUpdate = async (values: any) => {
        setLoading(prev => ({ ...prev, update: true }));
        try {
            const updatedCropData = {
                crop_name: values.cropName.trim(),
                crop_id: editCrop?.crop_id || "",
            };
            const response = await updateCrop(updatedCropData);
            if (response.status) {
                setCrops(prevCrops =>
                    prevCrops.map(crop =>
                        crop.crop_id === editCrop!.crop_id
                            ? { ...crop, ...updatedCropData }
                            : crop
                    )
                );

                successMessage(response.message || 'Crop updated successfully!');
                return true;
            } else {
                errorMessage(response.message || 'Failed to update crop. Please try again.');
                return false;
            }
        } catch (error) {
            console.error('Error updating crop:', error);
            const message = getErrorMessage(error);
            errorMessage(message || 'An unexpected error occurred. Please try again.');
            return false;
        } finally {
            setLoading(prev => ({ ...prev, update: false }));
        }
    };

    const handleDeleteConfirm = async () => {
        setLoading(prev => ({ ...prev, delete: true }));
        try {
            if (itemToDelete) {
                const response = await deleteCrop(itemToDelete.row.crop_id);
                if (response.status) {
                    setCrops(prev => prev.filter(crop => crop.crop_id !== itemToDelete.row.crop_id));
                    successMessage(response.message);
                } else {
                    errorMessage(response.message || 'Failed to delete crop.');
                }
            }
        } catch (error) {
            errorMessage(getErrorMessage(error) || 'Unexpected error occurred.');
        } finally {
            setLoading(prev => ({ ...prev, delete: false }));
            setDeleteOpen(false);
        }
    };

    return (
        <>
            {loading.fetch ? (
                <Loader color="primary" size={40} />
            ) : (
                <Box>
                    <main className="m-a-20">
                        <div className="d-flex justify-end">
                            <div className="d-flex justify-between w-100">
                                <TextField
                                    className="w-25"
                                    label="Search"
                                    variant="outlined"
                                    value={searchQuery}
                                    size="small"
                                    startIcon={<SearchIcon />}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                                <Button
                                    label="Add"
                                    variant="contained"
                                    color="primary"
                                    onClick={openDialog}
                                />
                            </div>
                        </div>
                        <div className="ag-theme-alpine m-t-20" style={{ height: '100%', width: '100%' }}>
                            <GridTable
                                rows={crops}
                                columns={columns}
                                pageSize={10}
                                searchQuery={searchQuery}
                                setSearchQuery={setSearchQuery}
                                getRowId={(row) => row.crop_id}
                                density="standard"
                            />
                        </div>
                        <Formik
                            initialValues={{
                                cropName: editCrop?.crop_name || "",
                            }}
                            validationSchema={validationSchema}
                            onSubmit={confirmDialog}
                            enableReinitialize
                        >
                            {({ values, errors, touched, handleChange, handleSubmit, resetForm }) => (
                                <Dialog
                                    open={isDialogOpen}
                                    onClose={() => closeDialog(resetForm)}
                                    onConfirm={handleSubmit}
                                    title={editCrop ? "Edit Crop" : "Add New Crop"}
                                    confirmButtonText={editCrop ? "Update" : "Add"}
                                    cancelButtonText="Cancel"
                                    icon={<CompostIcon />}
                                    message={
                                        <Box padding={4}>
                                            {loading.update || loading.create ? (
                                                <Loader color="primary" size={40} />
                                            ) : (
                                                <Form>
                                                    <Grid container spacing={2}>
                                                        <Grid size={12}>
                                                            <TextField
                                                                id="cropName"
                                                                name="cropName"
                                                                label="Crop Name"
                                                                variant="outlined"
                                                                fullWidth
                                                                value={values.cropName}
                                                                onChange={handleChange}
                                                                required
                                                                error={Boolean(touched.cropName && errors.cropName)}
                                                                helperText={touched.cropName && errors.cropName}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Form>
                                            )}
                                        </Box>
                                    }
                                />
                            )}
                        </Formik>
                    </main>
                </Box>
            )}
            <Dialog
                open={deleteOpen}
                onClose={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
                title={!loading.delete ? "Delete Crop" : ""}
                confirmButtonText={!loading.delete ? "Confirm" : ""}
                cancelButtonText={!loading.delete ? "Cancel" : ""}
                icon={<DeleteIcon />}
                message={
                    <Box padding={2}>
                        {loading.delete ? (
                            <Loader color="primary" size={40} />
                        ) : (
                            "Are you sure you want to delete this crop?"
                        )}
                    </Box>
                }
            />
        </>
    );
};

export default Crop;
